import { lazy, Suspense, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@jobenvoy/ui";
import UploadGuard from "@/components/tools/UploadGuard";
import type { ResumeVersion } from "@/lib/api/resumeService";
import { useQueueActions, type QueueAsk } from "@/lib/hooks/envoy";
import { keys } from "@/lib/hooks/keys";
import { ResumeFirstContext } from "@/lib/hooks/resumeFirstContext";
import { hasWorkHistory, RESUME_EMPTY, UPLOAD_UNLOADED } from "@/lib/tools/tailorAnswer";

const UploadDialog = lazy(() => import("@/components/resume/UploadDialog"));

/**
 * The upload, for a posting that waits on it. Sits outside the context, so
 * a second refusal here is said in words and does not open this again. A
 * tailor costs a credit, so a saved resume with nothing in it stops here.
 */
function WaitingPosting({ ask, onDone }: { ask: QueueAsk; onDone: () => void }) {
    const client = useQueryClient();
    const toast = useToast();
    const { add } = useQueueActions();

    const saved = () => {
        const baseline = client.getQueryData<ResumeVersion | null>(keys.resume.baseline);
        if (hasWorkHistory(baseline?.resume)) add.mutate(ask);
        else toast.error("Not added", RESUME_EMPTY);
    };

    return (
        <UploadGuard
            onFailed={() => {
                toast.error("Not added", UPLOAD_UNLOADED);
                onDone();
            }}
        >
            <Suspense fallback={null}>
                <UploadDialog open baseline={null} onSaved={saved} onClose={onDone} />
            </Suspense>
        </UploadGuard>
    );
}

/**
 * A Tailor from someone with no saved resume: every posting's Tailor comes
 * through the queue, which refuses it, so the upload opens here and the
 * posting is queued, as it was asked, once a resume is saved. Closing the
 * upload queues nothing.
 */
export function ResumeFirstProvider({ children }: { children: ReactNode }) {
    const [waiting, setWaiting] = useState<QueueAsk | null>(null);
    return (
        <>
            <ResumeFirstContext.Provider value={setWaiting}>{children}</ResumeFirstContext.Provider>
            {waiting && <WaitingPosting ask={waiting} onDone={() => setWaiting(null)} />}
        </>
    );
}
